import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import type {TexNoiseDimensions, TexNoiseType} from "#material/nodes/texture/tex-noise/types"
import {texNoiseDimensionsSchema, texNoiseTypeSchema} from "#material/nodes/texture/tex-noise/types"
import {keepLegacyColorMapping, keepLegacyTextureMapping} from "#material/nodes/texture/legacy-texture-settings"

export interface TexNoiseSockets extends MaterialNodeSockets {
    inputs: {
        detail: "float" // found in inputs: -; found in settings: float; inferred from data: float
        distortion: "float" // found in inputs: -; found in settings: float; inferred from data: float
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: point; found in settings: vector; inferred from data: float3
        gain: "float" // found in inputs: -; found in settings: float; inferred from data: float
        lacunarity: "float" // found in inputs: -; found in settings: float; inferred from data: float
        offset: "float" // found in inputs: -; found in settings: float; inferred from data: float
        roughness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        w: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        detail: "float" // found in inputs: -; found in settings: float; inferred from data: float
        distortion: "float" // found in inputs: -; found in settings: float; inferred from data: float
        fac: "float" // found in inputs: -; found in settings: float; inferred from data: float
        gain: "float" // found in inputs: -; found in settings: float; inferred from data: float
        lacunarity: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normalize: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        offset: "float" // found in inputs: -; found in settings: float; inferred from data: float
        roughness: "float" // found in inputs: -; found in settings: float; inferred from data: float
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: point; found in settings: vector; inferred from data: float3
        w: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settingsExtensions: {
        noiseDimensions?: TexNoiseDimensions // found in inputs: -; found in settings: string; inferred from data: string
        noiseType?: TexNoiseType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        color: "color"
        fac: "float"
    }
}

// one octave takes no shaping: roughness and lacunarity only tell the octaves apart
const IS_FRACTAL = {setting: "detail", whenSet: true} as const

export const texNoiseDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTexNoise,
    },
    nodeClass: "TexNoise",
    ui: {
        label: "Noise Texture",
        color: "#4987af",
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {name: "fac", valueType: "float", label: "Fac", output: true},
            {name: "vector", valueType: "point", label: "Vector", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {name: "scale", valueType: "float", label: "Scale", input: true, setting: true, defaultValue: 1},
            {name: "detail", valueType: "float", label: "Detail", input: true, setting: true, defaultValue: 2},
            {
                name: "roughness",
                valueType: "float",
                label: "Roughness",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0.5,
                visibleWhen: IS_FRACTAL,
            },
            {name: "lacunarity", valueType: "float", label: "Lacunarity", input: true, setting: true, defaultValue: 2, visibleWhen: IS_FRACTAL},
            {name: "distortion", valueType: "float", label: "Distortion", input: true, setting: true, defaultValue: 0},
            // {name: "scale", valueType: "float", label: "Scale"},
            // {name: "detail", valueType: "float", label: "Detail"},
            // {name: "distortion", valueType: "float", label: "Distortion"},
            {
                name: "noiseDimensions",
                defaultValue: "3D",
                label: "Dimensions",
                valueType: "string",
                options: [
                    {label: "1D", value: "1D"},
                    {label: "2D", value: "2D"},
                    {label: "3D", value: "3D"},
                    {label: "4D", value: "4D"},
                ],
                input: false,
                setting: true,
            },
            {
                name: "noiseType",
                defaultValue: "FBM",
                label: "Type",
                valueType: "string",
                options: [
                    {label: "Multifractal", value: "MULTIFRACTAL"},
                    {label: "Ridged Multifractal", value: "RIDGED_MULTIFRACTAL"},
                    {label: "Hybrid Multifractal", value: "HYBRID_MULTIFRACTAL"},
                    {label: "fBM", value: "FBM"},
                    {label: "Hetero Terrain", value: "HETERO_TERRAIN"},
                ],
                input: false,
                setting: true,
            },
            {name: "normalize", label: "Normalize", valueType: "boolean", defaultValue: true, input: false, setting: true},
        ],
        support: {
            cycles: {
                full: false,
                remark: "Some parameters unsupported.",
            },
            three: {full: false, remark: "2D noise over the UVs driven by scale alone; the vector input and the other parameters are ignored."},
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput, fac: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                vector: optionalMaterialInput,
                scale: optionalMaterialInput,
                detail: optionalMaterialInput,
                distortion: optionalMaterialInput,
                gain: optionalMaterialInput,
                lacunarity: optionalMaterialInput,
                offset: optionalMaterialInput,
                roughness: optionalMaterialInput,
                w: optionalMaterialInput,
            }),
            settings: z.strictObject({
                color: z.optional(color),
                ...keepLegacyColorMapping,
                detail: z.optional(z.number()),
                distortion: z.optional(z.number()),
                fac: z.optional(z.number()),
                gain: z.optional(z.number()),
                lacunarity: z.optional(z.number()),
                noiseDimensions: z.optional(texNoiseDimensionsSchema),
                noiseType: z.optional(texNoiseTypeSchema),
                normalize: z.optional(z.boolean()),
                offset: z.optional(z.number()),
                roughness: z.optional(z.number()),
                scale: z.optional(z.number()),
                ...keepLegacyTextureMapping,
                vector: z.optional(vec3),
                w: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TexNoiseSockets>
